import {
  useQueries,
  type QueryFunctionContext,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { AreaAggregation } from '../../types/domain';
import {
  dataApiClient,
  type AreaAggregationType,
} from '../../infrastructure/dataApi/client';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useMapVisibility } from '../SimViewer/useMapVisibility';
import { useSchemaComparison } from '../SimViewer/useSchemaComparison';

type UseAreaAggregationComparisonsData = {
  isLoading: boolean;
  aggregationsMap: Record<string, Map<string, number>>;
};

const EMPTY_DATA = new Map<string, never>();

const useAreaAggregationComparisonsData = (
  type: AreaAggregationType
): UseAreaAggregationComparisonsData => {
  const { currentSimDataId } = useCurrentSimDataId();
  const { comparisons } = useSchemaComparison();
  const { apiFilter } = useApiDataFilter();
  const { visibleAreaType } = useMapVisibility();

  const simDataIds = currentSimDataId ? [currentSimDataId, ...comparisons] : [];

  const results = useQueries({
    queries: simDataIds.map((simDataId: string) => {
      return {
        queryKey: [
          'areaAggregation',
          simDataId,
          apiFilter,
          type,
          visibleAreaType,
        ],
        queryFn: async ({
          signal,
        }: QueryFunctionContext): Promise<Map<string, number>> => {
          return dataApiClient
            .getAreaAggr(simDataId, apiFilter, type, visibleAreaType, signal)
            .then((items: AreaAggregation[]): Map<string, number> => {
              return new Map<string, number>(
                items.map((item: AreaAggregation): [string, number] => {
                  return [item.key_code, item.value];
                })
              );
            });
        },
        meta: { dataType: 'others' },
      };
    }),
  });

  return {
    isLoading: results.some((result: UseQueryResult): boolean => {
      return result.isFetching;
    }),
    aggregationsMap: Object.fromEntries(
      simDataIds.map(
        (simDataId: string, index: number): [string, Map<string, number>] => {
          return [simDataId, results[index].data ?? EMPTY_DATA];
        }
      )
    ),
  };
};

export { useAreaAggregationComparisonsData };
